<template>
	<div class="video-message-container" @click="handleVideoView(message)">
		<div v-if="isSending" class="video-thumbnail-wrapper">
			<img src="undefined" />
		</div>
		<div class="video-thumbnail-wrapper" v-else-if="isSuccess">
			<template>
				<img
					:src="resizeThumbnailUrl"
					:key="retryKey"
					class="api-thumbnail"
					@error="onThumbnailError"
					@load="onThumbnailLoaded"
					@click.stop="handleVideoView(message)"
				/>

				<div v-if="loading" class="loading-overlay">
					<i class="el-icon-loading"></i>
					<span>{{ $t("chat.loading") }}</span>
				</div>

				<div class="play-overlay" v-if="!loadError">
					<div class="play-button">
						<i class="el-icon-video-play"></i>
					</div>
				</div>

				<div class="video-info-overlay" v-if="!loadError && !loading">
					<div class="size-badge" v-if="message.length">
						{{ getFileLength() }}
					</div>
					<div class="video-type-badge">
						{{ getFileExtension().toUpperCase() }}
					</div>
				</div>
			</template>
		</div>
		<div v-else-if="isFailed" class="video-thumbnail-wrapper">
			<img :src="resizeThumbnailUrl" :key="'failed-' + retryKey" class="api-thumbnail" />
		</div>
	</div>
</template>

<script>
export default {
	name: "ChatMessageVideo",

	props: {
		message: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			loading: true,
			loadError: false,
			retryKey: 0,
		};
	},

	computed: {
		// 消息发送状态
		isSending() {
			return this.message._status === 0;
		},
		isSuccess() {
			return this.message._status === 1 || this.message._status === undefined;
		},
		isFailed() {
			return this.message._status === 2;
		},

		resizeThumbnailUrl() {
			// 优先使用后端签名的缩略图（视频快照参数已签入）；
			// OSS 私有化后禁止在返回的 URL 上追加 x-oss-process / _retry 等参数（会破坏签名导致 403），
			// 失败重试通过更换 :key 强制重新加载
			return this.message.attachment_thumbnail_url || this.message.attachment_url || "";
		},

		getFileExtension() {
			return () => {
				if (!this.message.attachment) return "VIDEO";
				const url = this.message.attachment.split("?")[0];
				const parts = url.split(".");
				return parts.length > 1 ? parts.pop().toLowerCase() : "video";
			};
		},

		getFileLength() {
			return () => {
				const duration = this.message.length || 0;
				return this.formatDuration(duration);
			};
		},
	},

	watch: {
		// 监听消息状态变化，成功后刷新封面
		"message._status"(newStatus, oldStatus) {
			if (oldStatus === 0 && newStatus === 1) {
				// 从发送中变为成功，重置加载状态
				this.loading = true;
				this.loadError = false;
				this.retryKey++; // 强制刷新封面
			}
		},

		// 监听封面URL变化
		"message.attachment_thumbnail_url"(newUrl) {
			if (newUrl && this.isSuccess) {
				this.loading = true;
				this.loadError = false;
			}
		},
	},

	mounted() {
		if (this.isSuccess && !this.message.attachment_thumbnail_url) {
			this.loadError = true;
			this.loading = false;
		}
	},

	methods: {
		formatDuration(seconds) {
			if (!seconds || isNaN(seconds)) return "00:00";
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = Math.floor(seconds % 60);
			return hours > 0
				? `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
				: `${minutes}:${secs.toString().padStart(2, "0")}`;
		},

		handleVideoView(message) {
			// 只有发送成功才能查看
			if (!this.isSuccess) return;
			this.$emit("handle-video-viewer", message);
		},

		onThumbnailLoaded() {
			this.loading = false;
			this.loadError = false;
		},

		onThumbnailError() {
			this.loadError = true;
			this.loading = false;
		},

		retryLoad() {
			if (!this.message.attachment_thumbnail_url) {
				this.loadError = true;
				this.loading = false;
				return;
			}

			this.loadError = false;
			this.loading = true;
			this.retryKey++;
		},
	},
};
</script>

<style lang="less" scoped>
.video-message-container {
	position: relative;
	display: inline-block;
	cursor: pointer;
	border-radius: 8px;
	overflow: hidden;
	transition: transform 0.2s ease;

	&:hover {
		transform: scale(1.02);
	}
}

.video-thumbnail-wrapper {
	position: relative;
	width: 150px;
	height: 150px;
	background-color: #f0f0f0;
	border-radius: 8px;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
}

.api-thumbnail {
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: opacity 0.3s ease;

	&:hover {
		opacity: 0.9;
	}
}

// 发送中状态
.sending-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: white;
	gap: 8px;

	i {
		font-size: 24px;
		animation: rotating 1s linear infinite;
	}

	span {
		font-size: 12px;
		color: rgba(255, 255, 255, 0.9);
	}
}

// 发送失败状态
.failed-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: background-color 0.3s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.8);
	}
}

.failed-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	text-align: center;
	color: white;

	> i {
		font-size: 28px;
		color: #ff6b6b;
	}

	.failed-text {
		font-size: 12px;
		color: rgba(255, 255, 255, 0.9);
	}

	.retry-btn {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-top: 4px;
		padding: 4px 12px;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 4px;
		font-size: 12px;
		transition: all 0.3s ease;

		&:hover {
			background: rgba(255, 255, 255, 0.3);
			transform: scale(1.05);
		}

		i {
			font-size: 14px;
			transition: transform 0.3s ease;
		}

		&:hover i {
			transform: rotate(180deg);
		}
	}
}

.error-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	cursor: pointer;
	transition: background-color 0.3s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.7);
	}
}

.error-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	text-align: center;
	transition: transform 0.2s ease;

	.error-overlay:hover & {
		transform: scale(1.05);
	}

	i {
		font-size: 24px;
		color: #ff6b6b;
		transition: color 0.3s ease;

		.error-overlay:hover & {
			color: #ff5252;
		}
	}

	.retry-text {
		font-size: 12px;
		color: white;
		user-select: none;
		transition: color 0.3s ease;

		.error-overlay:hover & {
			color: #f0f0f0;
		}
	}

	.retry-container {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		margin-top: 6px;
		font-size: 12px;

		.el-icon-refresh {
			font-size: 14px;
			transition: transform 0.3s ease;
		}

		&:hover .el-icon-refresh {
			transform: rotate(90deg);
		}
	}
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: white;
	gap: 8px;

	i {
		font-size: 20px;
		animation: rotating 1s linear infinite;
	}

	span {
		font-size: 12px;
	}
}

.play-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.3s ease;

	.video-message-container:hover & {
		opacity: 1;
	}
}

.play-button {
	width: 50px;
	height: 50px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;

	&:hover {
		background: rgba(255, 255, 255, 1);
		transform: scale(1.1);
	}

	i {
		font-size: 20px;
		color: #333;
		margin-left: 2px;
	}
}

.video-info-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
}

.size-badge {
	position: absolute;
	bottom: 8px;
	left: 8px;
	background: rgba(0, 0, 0, 0.7);
	color: white;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 10px;
}

.video-type-badge {
	position: absolute;
	top: 8px;
	right: 8px;
	background: rgba(74, 144, 226, 0.9);
	color: white;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 10px;
	font-weight: bold;
}

@keyframes rotating {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.video-thumbnail-wrapper {
		width: 120px;
		height: 120px;
	}

	.play-button {
		width: 40px;
		height: 40px;

		i {
			font-size: 16px;
		}
	}

	.size-badge,
	.video-type-badge {
		font-size: 9px;
		padding: 1px 4px;
	}
}
</style>
